import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableColumnDef,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  Chip,
  Switch,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Edit as EditIcon,
  VisibilityOutlined as VisibilityIcon,
  SwapHorizOutlined as SwapHorizIcon,
  QueryStatsOutlined as StatsIcon,
  MoreVert as DetailsIcon,
  Inventory2Outlined as AssetsIcon,
  MoneyOutlined as SubscriptionsIcon,
  LocationOn as AddressesIcon,
  Business as DivisionsIcon,
  People as MembersIcon,
  ConfirmationNumberOutlined as TicketsIcon,
} from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useSelector } from "react-redux";
import { RootState } from "store";
import SlideOverPanel from "components/SlideOverPanel";
import AddCustomers from "./AddCustomers";
import EditCustomer from "./EditCustomers";
import CustomerSectionOverlayContent, {
  CustomerSectionType,
} from "./CustomerSectionOverlayContent";
import AddAddress from "./AddAddress";
import AddAsset from "layouts/Assets/AddAsset";
import AddSubscription from "layouts/Subscriptions/AddSubscription";
import AddEmployee from "layouts/employees/AddEmployee";
import ViewTicket from "layouts/Ticket/ViewTicket";
import AdminAddTicket from "layouts/AdminTickets/AdminAddTicket";

interface Customer {
  customer_id: number;
  name: string;
  email: string;
  phone: string;
  is_priority: boolean;
  is_active: boolean;
  type: string;
}

interface Ticket {
  category_name: string;
  description: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  employee_name: string;
  created_at: string;
  status_name: string;
  is_escalated?: boolean;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: { index: number }[];
  tableData: any[];
}

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });


const formatDate = (dateString: string) => {
  const formateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", formateOptions).format(date);
};

// Name is now plain text (row click opens details popover)
const NameCell: React.FC<{ value: string }> = ({ value }) => <span>{value}</span>;

interface PrioritySwitchCellProps {
  value: boolean;
  data: Customer;
  onSwitchChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    data: Customer
  ) => void;
}

const PrioritySwitchCell: React.FC<PrioritySwitchCellProps> = ({
  value,
  data,
  onSwitchChange,
}) => {
  return (
    <Switch
      checked={value}
      onChange={(event) => onSwitchChange(event, data)}
      color="primary"
    />
  );
};

interface StatusSwitchCellProps {
  value: boolean;
  data: Customer;
  onStatusChange: (data: Customer, isActive: boolean) => void;
}

const StatusSwitchCell: React.FC<StatusSwitchCellProps> = ({
  value,
  data,
  onStatusChange,
}) => {
  return (
    <Switch
      checked={value}
      onChange={(event) => onStatusChange(data, event.target.checked)}
      color="primary"
    />
  );
};

interface CustomerCounts {
  assets: number;
  tickets: number;
  activeSubscriptions: number;
}

interface ActionsCellProps {
  customerData: Customer;
  onEdit: (customerId: number) => void;
  onSwap: (email: string) => void;
  onView: (customerId: number, type: string, name: string) => void;
  onOpenDetails: (customerId: number, type: string, anchorEl: HTMLElement) => void;
  fetchCustomerCounts: (id: number) => Promise<CustomerCounts>;
}

const StatsCountTooltip: React.FC<{
  customerId: number;
  fetchCounts: (id: number) => Promise<CustomerCounts>;
}> = ({ customerId, fetchCounts }) => {
  const [counts, setCounts] = React.useState<CustomerCounts | null>(null);
  const [loading, setLoading] = React.useState(false);

  const handleMouseEnter = () => {
    setLoading(true);
    fetchCounts(customerId)
      .then((c) => {
        setCounts(c);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const title = loading ? (
    "Loading…"
  ) : counts ? (
    <Box component="span" sx={{ display: "block", "& ul": { margin: 0, paddingLeft: 1.5 } }}>
      <ul>
        <li>Assets: {counts.assets}</li>
        <li>Tickets: {counts.tickets}</li>
        <li>Active subscriptions: {counts.activeSubscriptions}</li>
      </ul>
    </Box>
  ) : (
    "Counts"
  );

  return (
    <Tooltip title={title}>
      <IconButton size="small" aria-label="stats" onMouseEnter={handleMouseEnter}>
        <StatsIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const ActionsCell: React.FC<ActionsCellProps> = ({
  customerData,
  onEdit,
  onSwap,
  onView,
  onOpenDetails,
  fetchCustomerCounts,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => onEdit(customerData.customer_id)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Swap user">
        <IconButton size="small" aria-label="swap" onClick={() => onSwap(customerData.email)}>
          <SwapHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="View full profile">
        <IconButton
          size="small"
          aria-label="view"
          onClick={() =>
            onView(customerData.customer_id, customerData.type, customerData.name)
          }
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <StatsCountTooltip customerId={customerData.customer_id} fetchCounts={fetchCustomerCounts} />
      <Tooltip title="More (addresses, assets, subscriptions…)">
        <IconButton
          size="small"
          aria-label="details"
          onClick={(e) => onOpenDetails(customerData.customer_id, customerData.type, e.currentTarget)}
        >
          <DetailsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

interface DescriptionCellProps {
  value: string;
}

const DescriptionCell: React.FC<DescriptionCellProps> = ({ value }) => {
  const display = value?.length > 20 ? value.slice(0, 20) + "..." : value;
  return (
    <Tooltip title={value}>
      <span>{display}</span>
    </Tooltip>
  );
};

interface AddressCellProps {
  value: string;
  cityName: string;
  stateName: string;
  addressType: string;
}

const AddressCell: React.FC<AddressCellProps> = ({
  value,
  cityName,
  stateName,
  addressType,
}) => {
  const addressData = `${value}, ${cityName}, ${stateName}, ${addressType}`;
  const displayValue =
    addressData?.length > 20
      ? `${addressData.substring(0, 20)}...`
      : addressData;

  return (
    <Tooltip title={addressData}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

const renderNameCell = (value: string) => <NameCell value={value} />;

const renderPrioritySwitchCell = (
  value: boolean,
  tableMeta: TableMeta,
  handleSwitchChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    data: Customer
  ) => void
) => {
  const data = tableMeta.tableData[
    tableMeta.currentTableData[tableMeta.rowIndex]?.index
  ] as Customer;

  return (
    <PrioritySwitchCell
      value={value}
      data={data}
      onSwitchChange={handleSwitchChange}
    />
  );
};

const renderStatusSwitchCell = (
  value: boolean,
  tableMeta: TableMeta,
  handleStatus: (data: Customer, isActive: boolean) => void
) => {
  const data = tableMeta.tableData[
    tableMeta.currentTableData[tableMeta.rowIndex]?.index
  ] as Customer;

  return (
    <StatusSwitchCell value={value} data={data} onStatusChange={handleStatus} />
  );
};

const renderActionsCell = (
  _value: any,
  tableMeta: TableMeta,
  handlers: {
    handleEdit: (customerId: number) => void;
    handleSwap: (email: string) => void;
    handleView: (customerId: number, type: string, name: string) => void;
    onOpenDetails: (customerId: number, type: string, anchorEl: HTMLElement) => void;
    fetchCustomerCounts: (id: number) => Promise<CustomerCounts>;
  }
) => {
  const customerData = tableMeta.tableData[
    tableMeta.currentTableData[tableMeta.rowIndex]?.index
  ] as Customer;

  return (
    <ActionsCell
      customerData={customerData}
      onEdit={handlers.handleEdit}
      onSwap={handlers.handleSwap}
      onView={handlers.handleView}
      onOpenDetails={handlers.onOpenDetails}
      fetchCustomerCounts={handlers.fetchCustomerCounts}
    />
  );
};

const renderDescriptionCell = (value: string) => {
  return <DescriptionCell value={value} />;
};

const renderAddressCell = (value: string, tableMeta: TableMeta) => {
  const data = tableMeta.tableData[
    tableMeta.currentTableData[tableMeta.rowIndex]?.index
  ] as Ticket;

  return (
    <AddressCell
      value={value}
      cityName={data.city_name}
      stateName={data.state_name}
      addressType={data.address_type}
    />
  );
};

const CustomerList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const { fetchError, fetchSuccess } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<Customer[]>([]);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [ticketStatuses, setTicketStatuses] = React.useState<
    { status_id: number; status_name: string }[]
  >([]);
  const [ticketStatusFilter, setTicketStatusFilter] = React.useState<
    number | "all"
  >("all");
  const [subscriptionExpiring30, setSubscriptionExpiring30] =
    React.useState(false);
  const [customerTypeFilter, setCustomerTypeFilter] = React.useState<
    "all" | "customer" | "company"
  >("all");
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<number | null>(null);

  // Details menu (Assets, Subscriptions, Addresses, Divisions, Members)
  const [detailsAnchorEl, setDetailsAnchorEl] = React.useState<HTMLElement | null>(null);
  const [detailsCustomerId, setDetailsCustomerId] = React.useState<number | null>(null);
  const [detailsCustomerType, setDetailsCustomerType] = React.useState<string>("customer");

  // Section overlay (single section: addresses | assets | subscriptions | divisions | members)
  const [sectionOverlayOpen, setSectionOverlayOpen] = React.useState(false);
  const [sectionOverlayCustomerId, setSectionOverlayCustomerId] = React.useState<number | null>(null);
  const [sectionOverlaySection, setSectionOverlaySection] = React.useState<CustomerSectionType | null>(null);
  const [sectionOverlayTitle, setSectionOverlayTitle] = React.useState<string>("");
  const [sectionOverlayCustomerType, setSectionOverlayCustomerType] = React.useState<string>("customer");

  // Add Address overlay (opened from Addresses section; replaces section overlay so only one slide is open)
  const [addAddressOverlayOpen, setAddAddressOverlayOpen] = React.useState(false);
  const [addAddressCustomerId, setAddAddressCustomerId] = React.useState<number | null>(null);
  const [addAddressCustomerType, setAddAddressCustomerType] = React.useState<string>("customer");

  // Add Asset overlay (from Assets section; closes section overlay, customer pre-selected)
  const [addAssetOverlayOpen, setAddAssetOverlayOpen] = React.useState(false);
  const [addAssetCustomerId, setAddAssetCustomerId] = React.useState<number | null>(null);
  const [addAssetCustomerType, setAddAssetCustomerType] = React.useState<string>("customer");

  // Add Subscription overlay (from Subscriptions section; closes section overlay, customer pre-filled)
  const [addSubscriptionOverlayOpen, setAddSubscriptionOverlayOpen] = React.useState(false);
  const [addSubscriptionCustomerId, setAddSubscriptionCustomerId] = React.useState<number | null>(null);
  const [addSubscriptionCustomerType, setAddSubscriptionCustomerType] = React.useState<string>("customer");

  // Add Member overlay (from Members section; closes section overlay)
  const [addMemberOverlayOpen, setAddMemberOverlayOpen] = React.useState(false);
  const [addMemberCustomerId, setAddMemberCustomerId] = React.useState<number | null>(null);

  // Ticket detail overlay (from Tickets section; closes section overlay)
  const [ticketDetailOverlayOpen, setTicketDetailOverlayOpen] = React.useState(false);
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);

  // Add Ticket overlay (from Tickets section; closes section overlay, customer pre-selected)
  const [addTicketOverlayOpen, setAddTicketOverlayOpen] = React.useState(false);
  const [addTicketCustomerId, setAddTicketCustomerId] = React.useState<number | null>(null);

  const filteredRows = React.useMemo(() => {
    if (!status) return rows;

    if (status === "active") {
      return rows.filter((c) => Number(c.is_active) === 1);
    }

    if (status === "inactive") {
      return rows.filter((c) => Number(c.is_active) === 0);
    }

    return rows;
  }, [rows, status]);

  const handleSwap = async (userMail: string) => {
    const swapData = {
      actualEmail: user?.email,
      actualRole: user?.Role[0],
      actualRoleId: user?.roleId,
      actualName: user?.name,
      actualUserId: user?.userId,
      userRole: 5,
      userMail: userMail,
    };
    try {
      await axios.post("/auth/switch-user", swapData);
      globalThis.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCustomerCounts = React.useCallback(
    async (id: number): Promise<CustomerCounts> => {
      const res = await axios.get(`/customer/summary/${id}`);
      return {
        assets: Number(res?.data?.assets_count ?? 0),
        tickets: Number(res?.data?.tickets_count ?? 0),
        activeSubscriptions: Number(res?.data?.active_subscriptions_count ?? 0),
      };
    },
    []
  );

  const handleOpenDetails = React.useCallback(
    (id: number, type: string, anchorEl: HTMLElement) => {
      setDetailsCustomerId(id);
      setDetailsCustomerType(type);
      setDetailsAnchorEl(anchorEl);
    },
    []
  );

  const handleDetailsMenuClose = React.useCallback(() => {
    setDetailsAnchorEl(null);
    setDetailsCustomerId(null);
    setDetailsCustomerType("customer");
  }, []);

  const handleOpenSectionOverlay = React.useCallback(
    (section: CustomerSectionType, title: string) => {
      if (detailsCustomerId == null) return;
      setSectionOverlayCustomerId(detailsCustomerId);
      setSectionOverlaySection(section);
      setSectionOverlayTitle(title);
      setSectionOverlayCustomerType(detailsCustomerType);
      setSectionOverlayOpen(true);
      handleDetailsMenuClose();
    },
    [detailsCustomerId, detailsCustomerType, handleDetailsMenuClose]
  );

  const handleOpenAddAddressFromOverlay = React.useCallback(
    (customerId: number, customerType: string) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setAddAddressOverlayOpen(true);
      setAddAddressCustomerId(customerId);
      setAddAddressCustomerType(customerType);
    },
    []
  );

  const handleOpenAddAssetFromOverlay = React.useCallback(
    (customerId: number, customerType: string) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setAddAssetOverlayOpen(true);
      setAddAssetCustomerId(customerId);
      setAddAssetCustomerType(customerType);
    },
    []
  );

  const handleOpenAddSubscriptionFromOverlay = React.useCallback(
    (customerId: number, customerType: string) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setAddSubscriptionOverlayOpen(true);
      setAddSubscriptionCustomerId(customerId);
      setAddSubscriptionCustomerType(customerType);
    },
    []
  );

  const handleOpenAddMemberFromOverlay = React.useCallback(
    (customerId: number) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setAddMemberOverlayOpen(true);
      setAddMemberCustomerId(customerId);
    },
    []
  );

  const handleOpenTicketDetailFromOverlay = React.useCallback(
    (ticketId: number) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setTicketDetailOverlayOpen(true);
      setSelectedTicketId(ticketId);
    },
    []
  );

  const handleOpenAddTicketFromOverlay = React.useCallback(
    (customerId: number) => {
      setSectionOverlayOpen(false);
      setSectionOverlayCustomerId(null);
      setSectionOverlaySection(null);
      setSectionOverlayTitle("");
      setSectionOverlayCustomerType("customer");
      setAddTicketOverlayOpen(true);
      setAddTicketCustomerId(customerId);
    },
    []
  );

  const handleEdit = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setEditPanelOpen(true);
  };

  const handleView = (customerId: number, type: string, name: string) => {
    navigate(`/customer/view/${customerId}`, {
      state: { type, name },
    });
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: "name",
      label: "Name",
      options: {
        filter: false,
        customBodyRender: (value: string): React.ReactNode => renderNameCell(value),
      },
    },
    { name: "email", label: "Email" },
    { name: "phone", label: "Phone" },
    {
      name: "is_priority",
      label: "Premium",
      options: {
        filter: false,
        customBodyRender: (
          value: boolean,
          tableMeta: TableMeta
        ): React.ReactNode =>
          renderPrioritySwitchCell(value, tableMeta, handleSwitchChange),
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: (
          value: boolean,
          tableMeta: TableMeta
        ): React.ReactNode =>
          renderStatusSwitchCell(value, tableMeta, handleStatus),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (
          _value: any,
          tableMeta: TableMeta
        ): React.ReactNode =>
          renderActionsCell(_value, tableMeta, {
            handleEdit,
            handleSwap,
            handleView,
            onOpenDetails: handleOpenDetails,
            fetchCustomerCounts,
          }),
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const params: Record<string, string | number> = {};
      if (ticketStatusFilter !== "all") {
        params.ticket_status_id = ticketStatusFilter;
      }
      if (subscriptionExpiring30) {
        params.subscription_expiring_in_days = 30;
      }
      if (customerTypeFilter !== "all") {
        params.type = customerTypeFilter;
      }
      const response = await axios.get("/customer", { params });
      setRows(response?.data?.list || []);
    } catch {
      fetchError("Unable to get customers");
    }
  }, [fetchError, ticketStatusFilter, subscriptionExpiring30, customerTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    axios
      .get("/ticket-statuses")
      .then((res) =>
        setTicketStatuses(Array.isArray(res?.data) ? res.data : [])
      )
      .catch(() => setTicketStatuses([]));
  }, []);

  const handleSwitchChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    rowData: Customer
  ) => {
    const newValue = event.target.checked;
    try {
      await axios.put(`/customer/is-priority-update/${rowData?.customer_id}`, {
        is_priority: newValue,
      });
      fetchSuccess("Premium updated successfully");
      fetchData();
    } catch {
      fetchError("Unable to update premium");
    }
  };

  const handleStatus = async (rowData: Customer, isActive: boolean) => {
    try {
      await axios.put(`/customer/is-active/${rowData?.customer_id}`, {
        is_active: isActive,
      });
      fetchSuccess("Status updated successfully");
      fetchData();
    } catch {
      fetchError("Unable to update status");
    }
  };

  const tableOptions: MUIDataTableOptions = React.useMemo(
    () => ({
      selectableRows: "none",
      selectableRowsHeader: false,
      elevation: 0,
      tableBodyHeight: "auto",
      responsive: "standard",
      // NOTE: mui-datatables' TS types define onRowClick(rowData, rowMeta) only,
      // but at runtime it also passes the click event as a 3rd argument.
      onRowClick: function (
        rowData: any,
        rowMeta: { dataIndex: number; rowIndex: number }
      ) {
        const row = filteredRows[rowMeta.dataIndex];
        const event = (arguments as any)[2] as
          | React.MouseEvent<HTMLElement>
          | undefined;
        const anchorEl = (event?.currentTarget as HTMLElement | undefined) ??
          undefined;

        if (row && anchorEl) {
          handleOpenDetails(row.customer_id, row.type, anchorEl);
        }
      },
    }),
    [filteredRows, handleOpenDetails]
  );
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: 16 }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="label"
                variant="caption"
                fontWeight="bold"
                sx={{ display: "block", mb: 0.5 }}
              >
                Ticket status
              </Typography>
              <Autocomplete
                size="small"
                options={[
                  { label: "All customers", value: "all" as const },
                  ...ticketStatuses.map((s) => ({ label: s.status_name, value: s.status_id as number })),
                ]}
                getOptionLabel={(opt) => opt.label}
                value={
                  ticketStatusFilter === "all"
                    ? { label: "All customers", value: "all" }
                    : ticketStatuses.find((s) => s.status_id === ticketStatusFilter)
                      ? {
                          label: ticketStatuses.find((s) => s.status_id === ticketStatusFilter)!.status_name,
                          value: ticketStatusFilter,
                        }
                      : { label: "All customers", value: "all" }
                }
                onChange={(_, newValue) => {
                  const v = newValue?.value;
                  setTicketStatusFilter(v === "all" || v == null ? "all" : (v as number));
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: 2,
                    "& fieldset": { borderRadius: 2 },
                  },
                  "& .MuiOutlinedInput-input": { py: 0.75 },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select status" variant="outlined" />
                )}
              />
            </Box>
            <Chip
              label="Subscription expires in next 30 days"
              size="small"
              color={subscriptionExpiring30 ? "secondary" : "default"}
              variant={subscriptionExpiring30 ? "filled" : "outlined"}
              onClick={() => setSubscriptionExpiring30((prev) => !prev)}
              sx={{
                fontSize: "0.75rem",
                ml: 0.5,
                height: 40,
                borderRadius: 2,
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="label"
                variant="caption"
                fontWeight="bold"
                sx={{ display: "block", mb: 0.5 }}
              >
                Type
              </Typography>
              <Autocomplete
                size="small"
                options={[
                  { label: "All", value: "all" as const },
                  { label: "Customer", value: "customer" as const },
                  { label: "Company", value: "company" as const },
                ]}
                getOptionLabel={(opt) => opt.label}
                value={
                  customerTypeFilter === "all"
                    ? { label: "All", value: "all" }
                    : customerTypeFilter === "customer"
                      ? { label: "Customer", value: "customer" }
                      : { label: "Company", value: "company" }
                }
                onChange={(_, newValue) => {
                  const v = newValue?.value ?? "all";
                  setCustomerTypeFilter(v === "customer" || v === "company" ? v : "all");
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: 2,
                    "& fieldset": { borderRadius: 2 },
                  },
                  "& .MuiOutlinedInput-input": { py: 0.75 },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select type" variant="outlined" />
                )}
              />
            </Box>
          </Box>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={() => setAddPanelOpen(true)}
          >
            Add Customer
          </SoftButton>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Customers"}
              data={filteredRows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Customer"
        width={1000}
      >
        <AddCustomers
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedCustomerId(null);
        }}
        title="Edit Customer"
        width={1000}
      >
        {selectedCustomerId != null && (
          <EditCustomer
            id={String(selectedCustomerId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedCustomerId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedCustomerId(null);
            }}
          />
        )}
      </SlideOverPanel>

      {/* Details menu: Assets, Subscriptions, Addresses, Add Address, Divisions, Members */}
      <Menu
        open={Boolean(detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={handleDetailsMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 140,
            borderRadius: 1.5,
            py: 0.25,
            "& .MuiMenuItem-root": { minHeight: 32, py: 0.5 },
            "& .MuiListItemIcon-root": { minWidth: 28 },
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => handleOpenSectionOverlay("assets", "Assets")}
          sx={{ fontSize: "0.8125rem" }}
        >
          <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
            <AssetsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Assets" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenSectionOverlay("tickets", "Tickets")}
          sx={{ fontSize: "0.8125rem" }}
        >
          <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
            <TicketsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Tickets" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenSectionOverlay("subscriptions", "Subscriptions")}
          sx={{ fontSize: "0.8125rem" }}
        >
          <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
            <SubscriptionsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Subscriptions" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenSectionOverlay("addresses", "Addresses")}
          sx={{ fontSize: "0.8125rem" }}
        >
          <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
            <AddressesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Addresses" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
        </MenuItem>
        {detailsCustomerType === "company" && (
          <>
            <MenuItem
              onClick={() => handleOpenSectionOverlay("divisions", "Divisions")}
              sx={{ fontSize: "0.8125rem" }}
            >
              <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
                <DivisionsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Divisions" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
            </MenuItem>
            <MenuItem
              onClick={() => handleOpenSectionOverlay("members", "Members")}
              sx={{ fontSize: "0.8125rem" }}
            >
              <ListItemIcon sx={{ "& svg": { fontSize: "1rem" } }}>
                <MembersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Members" primaryTypographyProps={{ fontSize: "0.8125rem" }} />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Section overlay: single section (Assets, Addresses, etc.) */}
      <SlideOverPanel
        open={sectionOverlayOpen}
        onClose={() => {
          setSectionOverlayOpen(false);
          setSectionOverlayCustomerId(null);
          setSectionOverlaySection(null);
          setSectionOverlayTitle("");
          setSectionOverlayCustomerType("customer");
        }}
        title={sectionOverlayTitle}
        width={900}
      >
        {sectionOverlayCustomerId != null && sectionOverlaySection != null && (
          <CustomerSectionOverlayContent
            customerId={sectionOverlayCustomerId}
            section={sectionOverlaySection}
            customerType={
              sectionOverlaySection === "addresses" ||
              sectionOverlaySection === "assets" ||
              sectionOverlaySection === "subscriptions" ||
              sectionOverlaySection === "tickets"
                ? sectionOverlayCustomerType
                : undefined
            }
            onOpenAddAddress={
              sectionOverlaySection === "addresses"
                ? handleOpenAddAddressFromOverlay
                : undefined
            }
            onOpenAddAsset={
              sectionOverlaySection === "assets"
                ? handleOpenAddAssetFromOverlay
                : undefined
            }
            onOpenAddSubscription={
              sectionOverlaySection === "subscriptions"
                ? handleOpenAddSubscriptionFromOverlay
                : undefined
            }
            onOpenAddMember={
              sectionOverlaySection === "members"
                ? handleOpenAddMemberFromOverlay
                : undefined
            }
            onOpenTicketDetail={
              sectionOverlaySection === "tickets"
                ? handleOpenTicketDetailFromOverlay
                : undefined
            }
            onOpenAddTicket={
              sectionOverlaySection === "tickets"
                ? handleOpenAddTicketFromOverlay
                : undefined
            }
          />
        )}
      </SlideOverPanel>

      {/* Add Address overlay (replaces Addresses slide so only one slide is open) */}
      <SlideOverPanel
        open={addAddressOverlayOpen}
        onClose={() => {
          setAddAddressOverlayOpen(false);
          setAddAddressCustomerId(null);
          setAddAddressCustomerType("customer");
        }}
        title="Add Address"
        width={700}
      >
        {addAddressCustomerId != null && (
          <AddAddress
            onClose={() => {
              setAddAddressOverlayOpen(false);
              setAddAddressCustomerId(null);
              setAddAddressCustomerType("customer");
            }}
            customerId={String(addAddressCustomerId)}
            customerType={addAddressCustomerType}
            fetchData={fetchData}
            fetchSuccess={fetchSuccess}
            fetchError={fetchError}
          />
        )}
      </SlideOverPanel>

      {/* Add Ticket overlay (from Tickets section; customer pre-selected, single asset auto-filled) */}
      <SlideOverPanel
        open={addTicketOverlayOpen}
        onClose={() => {
          setAddTicketOverlayOpen(false);
          setAddTicketCustomerId(null);
        }}
        title="Add Ticket"
        width={700}
      >
        {addTicketCustomerId != null && (
          <AdminAddTicket
            embedded
            initialCustomerId={addTicketCustomerId}
            onSuccess={() => {
              setAddTicketOverlayOpen(false);
              setAddTicketCustomerId(null);
              fetchData();
            }}
            onCancel={() => {
              setAddTicketOverlayOpen(false);
              setAddTicketCustomerId(null);
            }}
          />
        )}
      </SlideOverPanel>

      {/* Add Asset overlay (from Assets section; customer pre-selected) */}
      <SlideOverPanel
        open={addAssetOverlayOpen}
        onClose={() => {
          setAddAssetOverlayOpen(false);
          setAddAssetCustomerId(null);
          setAddAssetCustomerType("customer");
        }}
        title="Add Asset"
        width={1000}
      >
        {addAssetCustomerId != null && (
          <AddAsset
            isOpen={true}
            onClose={() => {
              setAddAssetOverlayOpen(false);
              setAddAssetCustomerId(null);
              setAddAssetCustomerType("customer");
            }}
            customers={
              rows.filter((r) => r.customer_id === addAssetCustomerId).length > 0
                ? rows.filter((r) => r.customer_id === addAssetCustomerId)
                : [
                    {
                      customer_id: addAssetCustomerId,
                      name: "Customer",
                      email: "",
                      phone: "",
                      is_priority: false,
                      is_active: true,
                      type: addAssetCustomerType,
                    },
                  ]
            }
            fetchData={fetchData}
            initialCustomerId={addAssetCustomerId}
            embedded
          />
        )}
      </SlideOverPanel>

      {/* Add Subscription overlay (from Subscriptions section; customer pre-filled, single asset auto-filled) */}
      <SlideOverPanel
        open={addSubscriptionOverlayOpen}
        onClose={() => {
          setAddSubscriptionOverlayOpen(false);
          setAddSubscriptionCustomerId(null);
          setAddSubscriptionCustomerType("customer");
        }}
        title="Add Subscription"
        width={700}
      >
        {addSubscriptionCustomerId != null && (
          <AddSubscription
            embedded
            initialCustomerId={addSubscriptionCustomerId}
            onSuccess={() => {
              setAddSubscriptionOverlayOpen(false);
              setAddSubscriptionCustomerId(null);
              setAddSubscriptionCustomerType("customer");
              fetchData();
            }}
            onCancel={() => {
              setAddSubscriptionOverlayOpen(false);
              setAddSubscriptionCustomerId(null);
              setAddSubscriptionCustomerType("customer");
            }}
          />
        )}
      </SlideOverPanel>

      {/* Add Member overlay (from Members section) */}
      <SlideOverPanel
        open={addMemberOverlayOpen}
        onClose={() => {
          setAddMemberOverlayOpen(false);
          setAddMemberCustomerId(null);
        }}
        title="Add Member"
        width={700}
      >
        {addMemberCustomerId != null && (
          <AddEmployee
            embedded
            initialCustomerId={addMemberCustomerId}
            onSuccess={() => {
              setAddMemberOverlayOpen(false);
              setAddMemberCustomerId(null);
              fetchData();
            }}
            onCancel={() => {
              setAddMemberOverlayOpen(false);
              setAddMemberCustomerId(null);
            }}
          />
        )}
      </SlideOverPanel>

      {/* Ticket detail overlay (from Tickets section; closes tickets list overlay) */}
      <SlideOverPanel
        open={ticketDetailOverlayOpen}
        onClose={() => {
          setTicketDetailOverlayOpen(false);
          setSelectedTicketId(null);
        }}
        title="Ticket Details"
        width={900}
      >
        {selectedTicketId != null && (
          <ViewTicket
            embedded
            ticketId={selectedTicketId}
            onClose={() => {
              setTicketDetailOverlayOpen(false);
              setSelectedTicketId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default CustomerList;
